import React, { useEffect, useState } from 'react';
import SecretariatShell from './SecretariatShell';
import { SectionTitle } from './PageShell';
import { useAuth } from '../context/AuthContext';
import { api } from '../lib/api';
import { Loader2, FileDown, ShieldCheck, Check } from 'lucide-react';

const firstOfMonth = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`; };
const todayIso = () => new Date().toISOString().slice(0, 10);

export default function SecretariatDsrPage() {
  const { user, loading: authLoading } = useAuth();
  const [settings, setSettings] = useState({ school_name: '', numero_agrement: '' });
  const [savedOk, setSavedOk] = useState(false);
  const [saving, setSaving] = useState(false);
  const [start, setStart] = useState(firstOfMonth());
  const [end, setEnd] = useState(todayIso());
  const [downloading, setDownloading] = useState('');

  useEffect(() => {
    if (authLoading || !user) return;
    api.secSettings().then(setSettings).catch(() => {});
  }, [authLoading, user]);

  if (authLoading) return <SecretariatShell><div className="p-12 text-white/50">Chargement…</div></SecretariatShell>;
  if (!user || (user.role !== 'secretaire' && !user.is_admin && !user.secretariat_access)) return <SecretariatShell><div className="p-12 text-center text-white/60">Accès réservé au secrétariat.</div></SecretariatShell>;

  const saveSettings = async () => {
    setSaving(true);
    try {
      await api.secUpdateSettings(settings);
      setSavedOk(true); setTimeout(() => setSavedOk(false), 2200);
    } catch { /* noop */ }
    setSaving(false);
  };

  const download = async (mode) => {
    setDownloading(mode);
    try {
      const blob = await api.secExportDsr(start, end, mode);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `dsr-${mode}-${start}_${end}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { /* noop */ }
    setDownloading('');
  };

  const inputCls = 'mt-1 w-full h-10 rounded-lg bg-black/40 border border-white/15 px-3 text-white text-[13px]';

  return (
    <SecretariatShell>
      <section className="max-w-4xl mx-auto px-4 lg:px-6 mt-8" data-testid="secretariat-dsr">
        <SectionTitle kicker="Secrétariat · Conformité" title="Export DSR" sub="Données réglementaires exigées par la Direction de la Sécurité Routière : agrément, NEPH, autorisations d'enseigner, moyen utilisé et présences." />

        {/* Identité auto-école */}
        <div className="rounded-2xl border border-white/10 bg-[#0d0d0e] p-6 mb-5" data-testid="dsr-settings-card">
          <div className="flex items-center gap-2 mb-4">
            <ShieldCheck size={15} className="text-amber-400" />
            <h2 className="text-white text-[15px] font-semibold">Identité de l'auto-école</h2>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <label className="block text-[12px] text-white/55">Nom de l'établissement
              <input value={settings.school_name || ''} onChange={(e) => setSettings({ ...settings, school_name: e.target.value })} placeholder="Auto-école …" data-testid="dsr-school-name-input" className={inputCls} />
            </label>
            <label className="block text-[12px] text-white/55">Numéro d'agrément préfectoral (identifiant Aurige)
              <input value={settings.numero_agrement || ''} onChange={(e) => setSettings({ ...settings, numero_agrement: e.target.value })} placeholder="Ex : E 02 012 3456 0" data-testid="dsr-agrement-input" className={inputCls} />
            </label>
          </div>
          <div className="mt-4 flex items-start gap-3 rounded-xl border border-orange-700/30 bg-orange-950/10 p-3.5">
            <button
              onClick={() => setSettings({ ...settings, show_hours: !settings.show_hours })}
              data-testid="dsr-show-hours-toggle"
              className={`relative w-11 h-6 rounded-full transition shrink-0 mt-0.5 ${settings.show_hours ? 'bg-emerald-500' : 'bg-white/15'}`}
            >
              <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all ${settings.show_hours ? 'left-[22px]' : 'left-0.5'}`} />
            </button>
            <div className="text-[12.5px]">
              <div className="text-white font-semibold">Afficher les heures (effectuées / restantes) aux élèves et enseignants</div>
              <div className="text-white/50 mt-0.5">Désactivé par défaut : tant que les plannings ne sont pas synchronisés avec votre outil actuel (Codes Rousseau), les compteurs d'heures restent masqués pour éviter tout litige. Les exports DSR ne sont pas affectés.</div>
            </div>
          </div>
          <button onClick={saveSettings} disabled={saving} data-testid="dsr-save-settings-btn" className="mt-4 h-10 px-5 rounded-full bg-amber-500 hover:bg-amber-400 disabled:opacity-40 text-black text-[13px] font-bold inline-flex items-center gap-2">
            {saving ? <Loader2 size={14} className="animate-spin" /> : savedOk ? <Check size={14} /> : null}
            {savedOk ? 'Enregistré !' : 'Enregistrer'}
          </button>
        </div>

        {/* Exports */}
        <div className="rounded-2xl border border-white/10 bg-[#0d0d0e] p-6" data-testid="dsr-export-card">
          <div className="flex items-center gap-2 mb-4">
            <FileDown size={15} className="text-amber-400" />
            <h2 className="text-white text-[15px] font-semibold">Exports CSV (compatibles Excel / outils de l'État)</h2>
          </div>
          <div className="grid grid-cols-2 gap-3 max-w-md mb-5">
            <label className="block text-[12px] text-white/55">Du
              <input type="date" value={start} onChange={(e) => setStart(e.target.value)} data-testid="dsr-start-input" className={inputCls} />
            </label>
            <label className="block text-[12px] text-white/55">Au
              <input type="date" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="dsr-end-input" className={inputCls} />
            </label>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
              <div className="text-white text-[13.5px] font-semibold mb-1">Registre des leçons</div>
              <p className="text-white/50 text-[12px] leading-relaxed mb-3">Chaque leçon avec date, durée, moyen utilisé (BM / BA / simulateur), statut de présence, NEPH de l'élève et n° d'autorisation de l'enseignant.</p>
              <button onClick={() => download('lecons')} disabled={!!downloading} data-testid="dsr-export-lecons-btn" className="h-9 px-4 rounded-full bg-white text-black hover:bg-white/90 disabled:opacity-40 text-[12.5px] font-bold inline-flex items-center gap-2">
                {downloading === 'lecons' ? <Loader2 size={13} className="animate-spin" /> : <FileDown size={13} />} Télécharger le CSV
              </button>
            </div>
            <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
              <div className="text-white text-[13.5px] font-semibold mb-1">Synthèse heures par élève</div>
              <p className="text-white/50 text-[12px] leading-relaxed mb-3">Total des heures effectuées par élève, ventilées par moyen (boîte manuelle, automatique, simulateur) — format attendu pour le livret numérique.</p>
              <button onClick={() => download('synthese')} disabled={!!downloading} data-testid="dsr-export-synthese-btn" className="h-9 px-4 rounded-full bg-white text-black hover:bg-white/90 disabled:opacity-40 text-[12.5px] font-bold inline-flex items-center gap-2">
                {downloading === 'synthese' ? <Loader2 size={13} className="animate-spin" /> : <FileDown size={13} />} Télécharger le CSV
              </button>
            </div>
          </div>
          <p className="mt-4 text-white/35 text-[11.5px] leading-relaxed">
            💡 Renseignez le NEPH sur chaque fiche élève, le n° d'autorisation d'enseigner sur chaque fiche enseignant, et le moyen utilisé + statut de présence sur chaque leçon : ces champs alimentent directement les exports.
          </p>
        </div>
      </section>
    </SecretariatShell>
  );
}
